import Button from "components/ui/button";
import Input from "components/ui/input";
import { useEffect, useState } from "react";
import axiosInstance from "utils/axiosInstance";
import LiveStreamModal from "components/ui/liveStreamModal";
import LiveVideo from "./liveVideo";
import Notification from "./notification";

const CameraSettings = () => {
    const [cameras, setCameras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState({});
    const [editForm, setEditForm] = useState({});
    const [notification, setNotification] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addErrors, setAddErrors] = useState({});

    const [newCamera, setNewCamera] = useState({
        camera_id: "",
        name: "",
        description: "",
        is_active: true,
        urlType: "webcam",
        url: "0",
        camera_type: "both",
    });
    const handleNewCameraChange = (field, value) => {
        setNewCamera(prev => ({ ...prev, [field]: value }));
    };
    const validateNewCamera = () => {
        const errors = {};

        if (!newCamera.camera_id.trim()) {
            errors.camera_id = "Camera ID is required";
        } else if (newCamera.camera_id.length < 3) {
            errors.camera_id = "Camera ID must be at least 3 characters";
        }

        if (!newCamera.name.trim()) {
            errors.name = "Camera name is required";
        }

        if (!newCamera.camera_type) {
            errors.camera_type = "Camera type is required";
        }

        if (!newCamera.urlType) {
            errors.urlType = "Camera source is required";
        }

        if (newCamera.urlType === "rtsp") {
            if (!newCamera.url.trim()) {
                errors.url = "RTSP URL is required";
            }
        }

        setAddErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const addCameraSetting = async () => {
        if (!validateNewCamera()) return;
        try {
            const payload = {
                camera_id: newCamera.camera_id,
                name: newCamera.name,
                description: newCamera.description,
                is_active: newCamera.is_active,
                url: newCamera.urlType === "webcam" ? "0" : newCamera.url,
                camera_type: newCamera.camera_type,
            };

            await axiosInstance.post("/camera-settings/create", payload);

            showNotification("Camera added successfully", "success");
            setShowAddForm(false);
            fetchCameraSettings();

            // reset form
            setNewCamera({
                camera_id: "",
                name: "",
                description: "",
                is_active: true,
                urlType: "webcam",
                url: "0",
                camera_type: "both",
            });

        } catch (err) {
            console.error("Failed to add camera", err);
            showNotification("Failed to add camera", "error");
        }
    };

    const showNotification = (message, type = "info") => {
        setNotification({ message, type });
    };

    const closeNotification = () => {
        setNotification(null);
    };

    const fetchCameraSettings = async () => {
        try {
            const res = await axiosInstance.get("/camera-settings/getall");
            setCameras(res.data);

            const initialEditMode = {};
            const initialEditForm = {};

            res.data.forEach(camera => {
                initialEditMode[camera.id] = false;
                initialEditForm[camera.id] = {
                    ...camera,
                    urlType: camera.url === "0" ? "webcam" : "rtsp"
                };
            });

            setEditMode(initialEditMode);
            setEditForm(initialEditForm);
        } catch (err) {
            console.error("Failed to fetch camera settings", err);
            showNotification("Failed to fetch camera settings", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCameraSettings();
    }, []);

    const initializeCameras = async () => {
        try {
            await axiosInstance.post("/camera-settings/initialize");
            fetchCameraSettings();
            showNotification(`Cameras Initialized successfully`, "success");
        } catch (err) {
            showNotification(`Failed to initialize cameras`, "error");
            console.error("Failed to initialize cameras", err);
        }
    };

    const toggleEditMode = (cameraId) => {
        setEditMode(prev => ({ ...prev, [cameraId]: !prev[cameraId] }));
    };

    const handleFormChange = (cameraId, field, value) => {
        setEditForm(prev => ({
            ...prev,
            [cameraId]: { ...prev[cameraId], [field]: value }
        }));
    };

    const updateCameraSetting = async (cameraId) => {
        try {
            const camera = cameras.find(c => c.id === cameraId);
            await axiosInstance.put(`/camera-settings/update/${camera.camera_id}`, editForm[cameraId]);
            fetchCameraSettings();
            showNotification(`Camera settings updated successfully`, "success");
        } catch (err) {
            console.error("Failed to update camera setting", err);
            showNotification(`Failed to update camera setting`, "error");
        }
    };
    const toggleDelete = async (cameraId) => {
        try {
            const res = await axiosInstance.delete(`/camera-settings/delete/${cameraId}`);
            fetchCameraSettings();
            showNotification(res?.data?.message || ` camera deleted successfully`, "success");
            alert(res.data.message);
        } catch (err) {
            console.error("Failed to toggle camera active status", err);
            showNotification(`Failed to toggle camera active status`, "error");
        }
    };
    const toggleCameraActive = async (cameraId, cameraIdStr) => {
        try {
            const res = await axiosInstance.put(`/camera-settings/toggle-active/${cameraIdStr}`);
            fetchCameraSettings();
            showNotification(res?.data?.message || ` toggle camera active successfully`, "success");
            alert(res.data.message);
        } catch (err) {
            console.error("Failed to toggle camera active status", err);
            showNotification(`Failed to toggle camera active status`, "error");
        }
    };

    const getCameraTypeDisplay = (cameraType) => {
        switch (cameraType) {
            case "check_in": return "Check-in Only";
            case "check_out": return "Check-out Only";
            case "both": return "Check-in & Check-out";
            default: return "Check-in & Check-out";
        }
    };

    const formatUrlDisplay = (camera) => {
        if (!camera.url) return "Not set";
        const maxUrlLength = 40;
        const displayUrl = camera.url.length > maxUrlLength
            ? camera.url.substring(0, maxUrlLength) + "..."
            : camera.url;
        return `${camera.name}: ${displayUrl}`;
    };

    if (loading) {
        return <div className="flex justify-center p-8">Loading camera settings...</div>;
    }

    if (cameras.length === 0) {
        return (
            <div className="text-center p-8">
                <p className="mb-4">No camera settings found. Would you like to initialize default cameras?</p>
                <Button onClick={initializeCameras}>Initialize Cameras</Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">Camera Settings</h3>
                <div className="flex gap-2">
                    <Button onClick={() => setShowAddForm(true)}>Add Camera</Button>
                    <Button onClick={initializeCameras} variant="outline">
                        Reset to Default
                    </Button>
                </div>
            </div>
            {showAddForm && (
                <div className="border rounded-lg p-4 bg-gray-50 shadow-sm">
                    <h4 className="font-medium text-lg mb-4">Add New Camera</h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                        {/* Camera ID */}
                        <div>
                            <Input
                                placeholder="Camera ID"
                                value={newCamera.camera_id}
                                onChange={(e) => handleNewCameraChange("camera_id", e.target.value)}
                            />
                            {addErrors.camera_id && (
                                <p className="text-red-600 text-sm mt-1">{addErrors.camera_id}</p>
                            )}
                        </div>

                        {/* Camera Name */}
                        <div>
                            <Input
                                placeholder="Camera Name"
                                value={newCamera.name}
                                onChange={(e) => handleNewCameraChange("name", e.target.value)}
                            />
                            {addErrors.name && (
                                <p className="text-red-600 text-sm mt-1">{addErrors.name}</p>
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <Input
                                placeholder="Description"
                                value={newCamera.description}
                                onChange={(e) => handleNewCameraChange("description", e.target.value)}
                            />
                        </div>

                        {/* Camera Type */}
                        <div>
                            <select
                                className="w-full p-2 border rounded"
                                value={newCamera.camera_type}
                                onChange={(e) => handleNewCameraChange("camera_type", e.target.value)}
                            >
                                <option value="check_in">Check-in Only</option>
                                <option value="check_out">Check-out Only</option>
                                <option value="both">Check-in & Check-out</option>
                            </select>
                            {addErrors.camera_type && (
                                <p className="text-red-600 text-sm mt-1">{addErrors.camera_type}</p>
                            )}
                        </div>

                        {/* Camera Source */}
                        <div>
                            <select
                                className="w-full p-2 border rounded"
                                value={newCamera.urlType}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    handleNewCameraChange("urlType", val);
                                    handleNewCameraChange("url", val === "webcam" ? "0" : "");
                                }}
                            >
                                <option value="webcam">Webcam</option>
                                <option value="rtsp">RTSP</option>
                            </select>
                            {addErrors.urlType && (
                                <p className="text-red-600 text-sm mt-1">{addErrors.urlType}</p>
                            )}
                        </div>

                        {/* RTSP URL */}
                        <div>
                            <Input
                                placeholder="RTSP URL"
                                value={newCamera.url}
                                onChange={(e) => handleNewCameraChange("url", e.target.value)}
                                disabled={newCamera.urlType === "webcam"}
                            />
                            {addErrors.url && (
                                <p className="text-red-600 text-sm mt-1">{addErrors.url}</p>
                            )}
                        </div>

                    </div>

                    <div className="mt-6 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowAddForm(false)}>
                            Cancel
                        </Button>
                        <Button onClick={addCameraSetting}>
                            Add Camera
                        </Button>
                    </div>
                </div>
            )}


            {cameras.map(camera => (
                <div key={camera.id} className={`border rounded-lg p-4 bg-white shadow-sm ${!camera.is_active ? 'opacity-70' : ''}`}>

                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <div className="flex items-center">
                                <h4 className="font-medium text-lg">{camera.name}</h4>
                                <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${camera.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{camera.is_active ? 'Active' : 'Inactive'}</span>
                            </div>

                            <p className="text-sm text-gray-600">{camera.description}</p>

                            <div className="flex items-center mt-1">
                                <span className="text-sm font-medium mr-2">Camera ID:</span>
                                <span className="text-sm bg-gray-100 px-2 py-1 rounded">{camera.camera_id}</span>
                                <span className="ml-3 text-sm font-medium mr-2">Type:</span>
                                <span className="text-sm bg-blue-100 px-2 py-1 rounded text-blue-800">{getCameraTypeDisplay(camera.camera_type)}</span>
                            </div>
                        </div>

                        <div className="flex space-x-2">
                            <Button
                                onClick={() => toggleDelete(camera.camera_id)}
                                className="px-3 py-1 text-sm"
                            >
                                Delete
                            </Button>
                            <Button
                                onClick={() => toggleCameraActive(camera.id, camera.camera_id)}
                                variant={camera.is_active ? "destructive" : "default"}
                                className="px-3 py-1 text-sm"
                            >
                                {camera.is_active ? 'Deactivate' : 'Activate'}
                            </Button>

                            <Button
                                onClick={() => toggleEditMode(camera.id)}
                                variant={editMode[camera.id] ? "secondary" : "primary"}
                                className="px-3 py-1 text-sm"
                            >
                                {editMode[camera.id] ? "Cancel" : "Edit"}
                            </Button>

                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Camera Source</label>

                            {editMode[camera.id] ? (
                                <>
                                    <select
                                        className="w-full p-2 border rounded mb-2"
                                        value={editForm[camera.id].urlType}
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            handleFormChange(camera.id, "urlType", selected);

                                            if (selected === "webcam") {
                                                handleFormChange(camera.id, "url", "0");
                                            } else if (selected === "rtsp") {
                                                handleFormChange(camera.id, "url", camera.url || "");
                                            }
                                        }}
                                    >
                                        <option value="webcam">Webcam</option>
                                        <option value="rtsp">RTSP</option>
                                    </select>

                                    <Input
                                        value={editForm[camera.id].url || ""}
                                        onChange={(e) => handleFormChange(camera.id, "url", e.target.value)}
                                        disabled={editForm[camera.id].urlType === "webcam"}
                                    />
                                </>
                            ) : (
                                <div className="p-2 bg-gray-50 rounded border">{formatUrlDisplay(camera)}</div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Camera Type</label>
                            {editMode[camera.id] ? (
                                <select
                                    className="w-full p-2 border rounded"
                                    value={editForm[camera.id].camera_type || "both"}
                                    onChange={(e) => handleFormChange(camera.id, "camera_type", e.target.value)}
                                >
                                    <option value="check_in">Check-in Only</option>
                                    <option value="check_out">Check-out Only</option>
                                    <option value="both">Check-in & Check-out</option>
                                </select>
                            ) : (
                                <div className="p-2 bg-gray-50 rounded border">{getCameraTypeDisplay(camera.camera_type)}</div>
                            )}
                        </div>
                    </div>

                    {editMode[camera.id] && (
                        <div className="mt-4 flex justify-end space-x-2">
                            <Button variant="outline" onClick={() => toggleEditMode(camera.id)}>Cancel</Button>
                            <Button onClick={() => updateCameraSetting(camera.id)}>Save Changes</Button>
                        </div>
                    )}
                </div>
            ))}

            {notification && (
                <Notification message={notification.message} type={notification.type} onClose={closeNotification} />
            )}

        </div>
    );
};

export default CameraSettings;
